import { DateTime } from 'luxon';
import { convert12To24Hour } from './timezoneHelpers';

/**
 * Combine a user-chosen date + time string into a UTC epoch-ms timestamp,
 * interpreting the clock time in the car/listing timezone (never the browser TZ).
 *
 * Calendar pickers emit a Date at local midnight or noon. Those Y/M/D values are
 * the day the user picked, so we keep them and apply [timeString] in [timezone].
 * Instant/ISO Dates still take Y/M/D from the instant converted into [timezone].
 */
const combinePickerDateTimeGetUTC = (date: Date, timeString: string, timezone: string): number => {
  const time24Hour =
    timeString.includes('AM') || timeString.includes('PM')
      ? convert12To24Hour(timeString)
      : timeString;

  const [hours, minutes] = time24Hour.split(':').map(Number);

  // Prefer the picker's local calendar Y/M/D (react-calendar / <input type="date">
  // emit midnight-or-noon in the *browser* zone). Falling back to the instant
  // converted into the listing zone keeps ISO/URL Date inputs working.
  const isCalendarDate =
    date.getHours() === 0 ||
    (date.getHours() === 12 && date.getMinutes() === 0 && date.getSeconds() === 0);

  const zoned = DateTime.fromJSDate(date).setZone(timezone);
  const year = isCalendarDate ? date.getFullYear() : zoned.year;
  const month = isCalendarDate ? date.getMonth() + 1 : zoned.month;
  const day = isCalendarDate ? date.getDate() : zoned.day;

  const targetDate = DateTime.fromObject(
    {
      year,
      month,
      day,
      hour: hours,
      minute: minutes,
      second: 0,
      millisecond: 0,
    },
    { zone: timezone },
  );

  if (!targetDate.isValid) {
    console.warn(`Invalid timezone or date: ${timezone}`, targetDate.invalidReason);
  }

  return targetDate.toMillis();
};

/**
 * The calendar day (`yyyy-MM-dd`) a picker Date stands for, using the same rule
 * as [combinePickerDateTimeGetUTC]: local midnight or noon is a calendar date and
 * keeps the browser's Y/M/D; any other instant is read in [timezone].
 */
const pickerDateToDayKey = (date: Date, timezone: string): string => {
  const isCalendarDate =
    date.getHours() === 0 ||
    (date.getHours() === 12 && date.getMinutes() === 0 && date.getSeconds() === 0);
  if (isCalendarDate) {
    return (
      DateTime.fromObject({
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate(),
      }).toISODate() || ''
    );
  }
  return DateTime.fromJSDate(date).setZone(timezone).toISODate() || '';
};

export { combinePickerDateTimeGetUTC, pickerDateToDayKey };
